import React, { useState, useCallback, useMemo } from "react";
import {
  Plus,
  Trash2,
  Copy,
  Download,
  AlertCircle,
  CheckCircle,
  Upload,
  Monitor,
  X,
  FileJson,
} from "lucide-react";
import { toast } from "react-toastify";
import { LIMITS } from "../../utils/flowConstants";
import { validateFlow } from "../../utils/flowValidation";
import {
  createBlankScreen,
  createStarterFlow,
  buildFlowJSON,
  exportFlowJSON,
  uid,
} from "../../utils/flowJsonBuilder";
import ScreenEditor from "./ScreenEditor";
import FlowPreview from "./FlowPreview";

const MetaFlowBuilder = ({ initialFlow, onSave, onBack, onChange }) => {
  const [flow, setFlowInternal] = useState(
    () => initialFlow || createStarterFlow()
  );
  const setFlow = useCallback((updater) => {
    setFlowInternal((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (onChange) onChange(next);
      return next;
    });
  }, [onChange]);
  const [activeScreenIdx, setActiveScreenIdx] = useState(0);
  const [showValidation, setShowValidation] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const screens = flow.screens || [];
  const activeScreen = screens[activeScreenIdx] || null;

  const validation = useMemo(() => {
    const json = buildFlowJSON(flow);
    return validateFlow(json);
  }, [flow]);

  // ────── Screen operations ──────

  const addScreen = useCallback(() => {
    if (screens.length >= LIMITS.MAX_SCREENS) {
      toast.warning(`Maximum ${LIMITS.MAX_SCREENS} screens allowed`);
      return;
    }
    const existingIds = screens.map((s) => s.id);
    const newScreen = createBlankScreen(existingIds);
    setFlow((prev) => ({
      ...prev,
      screens: [...prev.screens, newScreen],
    }));
    setActiveScreenIdx(screens.length);
  }, [screens]);

  const deleteScreen = useCallback(
    (idx) => {
      if (screens.length <= LIMITS.MIN_SCREENS) {
        toast.warning("Flow must have at least 1 screen");
        return;
      }
      const screenId = screens[idx]?.id;
      const isNavTarget = screens.some((s) => {
        const children = s.layout?.children || [];
        return children.some((c) => {
          if (c.type === "Form") {
            return (c.children || []).some(
              (fc) => fc["on-click-action"]?.next?.name === screenId
            );
          }
          return c["on-click-action"]?.next?.name === screenId;
        });
      });
      if (isNavTarget) {
        toast.error(
          `Cannot delete "${screenId}" — other screens navigate to it. Update navigation first.`
        );
        return;
      }
      setFlow((prev) => ({
        ...prev,
        screens: prev.screens.filter((_, i) => i !== idx),
      }));
      setActiveScreenIdx((prev) => Math.min(prev, screens.length - 2));
    },
    [screens]
  );

  const duplicateScreen = useCallback(
    (idx) => {
      if (screens.length >= LIMITS.MAX_SCREENS) {
        toast.warning(`Maximum ${LIMITS.MAX_SCREENS} screens allowed`);
        return;
      }
      const original = screens[idx];
      const existingIds = screens.map((s) => s.id);
      let newId = `${original.id}_COPY`;
      let counter = 1;
      while (existingIds.includes(newId)) {
        newId = `${original.id}_COPY_${counter}`.slice(0, 30);
        counter++;
      }
      const dupe = JSON.parse(JSON.stringify(original));
      dupe.id = newId;
      dupe._uid = uid();
      dupe.title = `${original.title || ""} (Copy)`;
      setFlow((prev) => ({
        ...prev,
        screens: [...prev.screens, dupe],
      }));
      setActiveScreenIdx(screens.length);
    },
    [screens]
  );

  const updateScreen = useCallback(
    (updated) => {
      setFlow((prev) => ({
        ...prev,
        screens: prev.screens.map((s, i) =>
          i === activeScreenIdx ? updated : s
        ),
      }));
    },
    [activeScreenIdx]
  );

  // ────── Export ──────

  const copyJson = useCallback(() => {
    const { json, validation: v } = exportFlowJSON(flow);
    if (!v.valid) {
      toast.error("Fix validation errors before exporting");
      setShowValidation(true);
      return;
    }
    navigator.clipboard.writeText(JSON.stringify(json, null, 2));
    toast.success("JSON copied to clipboard");
  }, [flow]);

  const downloadJson = useCallback(() => {
    const { json, validation: v } = exportFlowJSON(flow);
    if (!v.valid) {
      toast.error("Fix validation errors before exporting");
      setShowValidation(true);
      return;
    }
    const blob = new Blob([JSON.stringify(json, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flow.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [flow]);

  const importJson = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target.result);
          if (!parsed.version || !parsed.screens) {
            toast.error("Invalid flow JSON structure");
            return;
          }
          // Add _uid to all nodes
          const addUids = (obj) => {
            if (Array.isArray(obj)) return obj.map(addUids);
            if (obj && typeof obj === "object") {
              const out = { ...obj, _uid: uid() };
              Object.keys(out).forEach((k) => {
                if (Array.isArray(out[k])) out[k] = out[k].map(addUids);
                else if (out[k] && typeof out[k] === "object" && k !== "on-click-action" && k !== "next")
                  out[k] = addUids(out[k]);
              });
              return out;
            }
            return obj;
          };
          const imported = addUids(parsed);
          setFlow(imported);
          setActiveScreenIdx(0);
          toast.success("Flow imported successfully");
        } catch {
          toast.error("Failed to parse JSON file");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  // ────── Render ──────

  const jsonSize = useMemo(() => {
    const str = JSON.stringify(buildFlowJSON(flow));
    return (new Blob([str]).size / 1024).toFixed(1);
  }, [flow]);

  return (
    <div className="waf-ed">
      <div className="waf-ed-tools">
        <span className="ping-chip n">
          {screens.length} screen{screens.length !== 1 ? "s" : ""} · {jsonSize} KB
        </span>
        {!validation.valid && (
          <span className="ping-chip r">{validation.errors.length} errors</span>
        )}
        <div className="grow" />
        <button type="button" className="camp-ico" onClick={importJson} data-tip="Import JSON" aria-label="Import JSON">
          <Upload size={14} />
        </button>
        <button
          type="button"
          className={`camp-ico ${showValidation ? "on" : ""} ${!validation.valid ? "danger" : ""}`}
          onClick={() => setShowValidation((v) => !v)}
          data-tip={validation.valid ? "Valid" : "Errors"}
          aria-label="Validation"
        >
          {validation.valid ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
        </button>
        <button
          type="button"
          className={`camp-ico ${showJson ? "on" : ""}`}
          onClick={() => setShowJson((v) => !v)}
          data-tip="JSON"
          aria-label="JSON"
        >
          <FileJson size={14} />
        </button>
        <button type="button" className="camp-ico" onClick={copyJson} data-tip="Copy JSON" aria-label="Copy JSON">
          <Copy size={14} />
        </button>
        <button type="button" className="camp-ico" onClick={downloadJson} data-tip="Download JSON" aria-label="Download JSON">
          <Download size={14} />
        </button>
      </div>

      {showValidation && (
        <div className="waf-ed-panel">
          <div className="waf-ed-panel-head">
            <strong>Validation</strong>
            <button type="button" className="camp-ico" onClick={() => setShowValidation(false)} aria-label="Close">
              <X size={14} />
            </button>
          </div>
          {validation.errors.map((err, i) => (
            <p key={`e-${i}`} className="waf-ed-err"><AlertCircle size={12} /> {err}</p>
          ))}
          {validation.warnings.map((w, i) => (
            <p key={`w-${i}`} className="waf-ed-warn"><AlertCircle size={12} /> {w}</p>
          ))}
          {validation.valid && validation.warnings.length === 0 && (
            <p className="waf-ed-ok"><CheckCircle size={12} /> Flow is valid and ready to export</p>
          )}
        </div>
      )}

      {showJson && (
        <pre className="waf-ed-json">{JSON.stringify(buildFlowJSON(flow), null, 2)}</pre>
      )}

      <div className="waf-ed-body">
        <aside className="waf-screens">
          <div className="waf-screens-head">
            <span>Screens</span>
            <button
              type="button"
              className="camp-ico"
              onClick={addScreen}
              disabled={screens.length >= LIMITS.MAX_SCREENS}
              data-tip="Add screen"
              aria-label="Add screen"
            >
              <Plus size={14} />
            </button>
          </div>
          <div className="waf-screens-list">
            {screens.map((s, i) => (
              <div
                key={s._uid || s.id}
                role="button"
                tabIndex={0}
                onClick={() => setActiveScreenIdx(i)}
                onKeyDown={(e) => { if (e.key === "Enter") setActiveScreenIdx(i); }}
                className={`waf-screen ${i === activeScreenIdx ? "on" : ""}`}
              >
                <div className="min-w-0">
                  <b>{s.title || s.id}</b>
                  <em>{s.id}</em>
                </div>
                <span className="waf-screen-acts">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); duplicateScreen(i); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); duplicateScreen(i); } }}
                    title="Duplicate"
                  >
                    <Copy size={11} />
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); deleteScreen(i); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); deleteScreen(i); } }}
                    title="Delete"
                  >
                    <Trash2 size={11} />
                  </span>
                </span>
                {s.terminal && <i>Terminal</i>}
              </div>
            ))}
          </div>
          <div className="waf-screens-foot">{screens.length}/{LIMITS.MAX_SCREENS}</div>
        </aside>

        <section className="waf-editor">
          {activeScreen ? (
            <ScreenEditor
              screen={activeScreen}
              screens={screens}
              onUpdate={updateScreen}
            />
          ) : (
            <div className="waf-editor-empty">Select a screen or create a new one</div>
          )}
        </section>

        <aside className="waf-preview">
          <div className="waf-preview-head">
            <Monitor size={13} />
            Preview
          </div>
          <FlowPreview flow={flow} />
        </aside>
      </div>
    </div>
  );
};

export default MetaFlowBuilder;
